import { initializePaddle, type Paddle, type PricePreviewParams } from '@paddle/paddle-js';

import { planOffers, upgradeOffer } from '../../src/plans';

import './styles.css';
import { renderDesktopFailure, startDesktopCheckout } from './desktopCheckout';
import { openWebsitePurchase, purchaseSuccessUrl, renderWebsiteReceipt, websiteCopy } from './websitePurchase';
import {
  copyFor,
  directionFor,
  publicLocaleOptions,
  matchSupportedLocale,
  paddleLocaleFor,
  resolveLocale,
  urlWithLocale,
  type CheckoutCopy,
  type SupportedLocale,
} from './i18n';
import {
  checkoutApiErrorCode,
  checkoutFailureView,
  formattedTotalsByPrice,
  parsePublicCheckoutConfig,
  previewOffers,
  transactionIdFromSearch,
  type PhotoKeeperOffer,
  type PublicCheckoutConfig,
} from './model';

const publicSiteOrigin = 'https://photokeeper.app';
const publishedPriceByOffer = {
  personal: `US$${planOffers[0].price.amount}`,
  family: `US$${planOffers[1].price.amount}`,
  upgrade: `US$${upgradeOffer.price.amount}`,
} as const;

const localeStorageKey = 'photokeeper.checkout.locale';
const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('PhotoKeeper checkout root is missing.');

const locale = resolveLocale(
  window.location.search,
  readStoredLocale(),
  navigator.languages.length > 0 ? navigator.languages : [navigator.language],
);
applyDocumentLocale(locale);

if (window.location.pathname === '/welcome' || window.location.pathname === '/welcome/') {
  renderWelcome(root, locale);
} else {
  void startPricingPage(root, locale);
}

async function startPricingPage(
  container: HTMLElement,
  currentLocale: SupportedLocale,
): Promise<void> {
  const copy = copyFor(currentLocale);
  const desktop = new URLSearchParams(window.location.search).get('display') === 'app';
  renderLoading(container, currentLocale);
  let transactionId: string | undefined;
  try {
    transactionId = transactionIdFromSearch(window.location.search);
    if (desktop && !transactionId) throw new Error('missing_desktop_transaction');
  } catch {
    if (desktop) renderDesktopFailure(container, currentLocale, copy.invalidLink);
    else renderFatal(container, currentLocale, copy.invalidLink);
    return;
  }

  try {
    const config = await loadConfig(transactionId);
    if (desktop) {
      await startDesktopCheckout(container, config, currentLocale);
      return;
    }
    const view = renderPricing(container, config, currentLocale);
    const paddle = await initializePaddle({
      token: config.clientToken,
      environment: config.environment,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed') {
          view.setStatus(copy.paymentCompleted);
        }
        if (event.name === 'checkout.error' || event.name === 'checkout.payment.error') {
          view.setStatus(copy.checkoutError, true);
        }
      },
    });
    if (!paddle) throw new Error('paddle_initialization_failed');

    try {
      view.setPrices(await previewPrices(paddle, config));
    } catch {
      view.setPrices({});
      view.setStatus(copy.priceLoadError, true);
      return;
    }
    view.enableCheckout((offer) =>
      openCheckout(paddle, config, view.setStatus, currentLocale, offer),
    );

    if (config.checkout) {
      openCheckout(paddle, config, view.setStatus, currentLocale);
    } else {
      const selected = new URL(window.location.href).searchParams.get('plan');
      const offer = config.offers.find(item => item.id === selected);
      if (offer?.kind === 'standalone-license') openWebsitePurchase(offer, currentLocale);
    }
  } catch (error) {
    if (desktop) {
      const gone = error instanceof CheckoutConfigError &&
        checkoutFailureView(error.status, error.code, Boolean(transactionId)) === 'checkout-gone';
      renderDesktopFailure(container, currentLocale, gone ? copy.checkoutGone : copy.pricingUnavailable);
      return;
    }
    if (error instanceof CheckoutConfigError) {
      const failureView = checkoutFailureView(
        error.status,
        error.code,
        Boolean(transactionId),
      );
      if (failureView === 'checkout-closed') {
        renderCheckoutClosed(container, currentLocale);
        return;
      }
      renderFatal(
        container,
        currentLocale,
        failureView === 'checkout-gone' ? copy.checkoutGone : copy.pricingUnavailable,
      );
      return;
    }
    renderFatal(container, currentLocale, copy.pricingUnavailable);
  }
}

async function loadConfig(transactionId?: string): Promise<PublicCheckoutConfig> {
  const url = new URL('/v1/checkout-config', window.location.origin);
  if (transactionId) url.searchParams.set('transactionId', transactionId);
  const response = await fetch(url, {
    method: 'GET',
    headers: { accept: 'application/json' },
    cache: 'no-store',
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
  });
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new CheckoutConfigError(response.status);
  }
  if (!response.ok) {
    throw new CheckoutConfigError(response.status, checkoutApiErrorCode(payload));
  }
  return parsePublicCheckoutConfig(payload, transactionId);
}

async function previewPrices(
  paddle: Paddle,
  config: PublicCheckoutConfig,
): Promise<Record<string, string>> {
  const params: PricePreviewParams = {
    items: previewOffers(config).map((offer) => ({ priceId: offer.priceId, quantity: 1 })),
    ...(config.countryCode ? { address: { countryCode: config.countryCode } } : {}),
  };
  return formattedTotalsByPrice(await paddle.PricePreview(params));
}

function openCheckout(
  paddle: Paddle,
  config: PublicCheckoutConfig,
  setStatus: (message: string, error?: boolean, attention?: boolean) => void,
  currentLocale: SupportedLocale,
  requestedOffer?: PhotoKeeperOffer,
): void {
  const copy = copyFor(currentLocale);
  if (!config.checkout) {
    if (requestedOffer?.kind === 'standalone-license') {
      openWebsitePurchase(requestedOffer, currentLocale);
      return;
    }
    setStatus(
      requestedOffer?.kind === 'license-upgrade'
        ? copy.upgradeRequiresSignIn
        : copy.purchaseRequiresSignIn,
      false,
      true,
    );
    return;
  }
  setStatus(copy.openingCheckout);
  paddle.Checkout.open({
    transactionId: config.checkout.transactionId,
    settings: {
      displayMode: 'overlay',
      variant: 'one-page',
      locale: paddleLocaleFor(currentLocale),
      successUrl: purchaseSuccessUrl(config.checkout.transactionId, currentLocale),
    },
  });
}

function renderPricing(
  container: HTMLElement,
  config: PublicCheckoutConfig,
  currentLocale: SupportedLocale,
): {
  setPrices: (prices: Record<string, string>) => void;
  enableCheckout: (open: (offer: PhotoKeeperOffer) => void) => void;
  setStatus: (message: string, error?: boolean, attention?: boolean) => void;
} {
  const copy = copyFor(currentLocale);
  container.replaceChildren();
  const shell = element('main', 'site-shell');
  shell.append(languageToolbar(currentLocale));

  const header = element('header', 'hero');
  header.append(
    textElement('span', 'eyebrow', copy.heroEyebrow),
    textElement('h1', '', copy.heroTitle),
    textElement('p', 'hero-copy', copy.heroDescription),
  );
  if (config.environment === 'sandbox') {
    header.append(textElement('span', 'sandbox-badge', copy.sandboxBadge));
  }

  const status = textElement('p', 'page-status', copy.loadingPrices);
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const grid = element('section', 'offer-grid');
  grid.setAttribute('aria-label', copy.offersLabel);
  const cards = new Map<string, ReturnType<typeof offerCard>>();
  for (const offer of config.offers) {
    const card = offerCard(offer, config.checkout?.offerId === offer.id, copy);
    cards.set(offer.priceId, card);
    grid.append(card.root);
  }

  const upgradeSection = element('section', 'upgrade-section');
  const upgradeSelected = config.checkout?.offerId === 'upgrade';
  const upgradeCard = offerCard(config.upgrade, upgradeSelected, copy);
  upgradeCard.root.classList.add('upgrade-card');
  if (!upgradeSelected) {
    upgradeCard.button.textContent = copy.signInToUpgrade;
  }
  cards.set(config.upgrade.priceId, upgradeCard);
  upgradeSection.append(
    textElement('span', 'eyebrow', copy.upgradeEyebrow),
    textElement('h2', '', copy.upgradeTitle),
    textElement('p', 'section-copy', copy.upgradeDescription),
    upgradeCard.root,
  );

  const trust = element('section', 'trust-panel');
  trust.append(
    textElement('strong', '', copy.trustTitle),
    textElement('p', '', copy.trustDescription),
    textElement('p', 'refund-policy', refundPolicyCopy(currentLocale)),
  );

  const footer = element('footer', 'site-footer');
  footer.append(policyNavigation(currentLocale), textElement('p', '', copy.disclaimer));

  shell.append(header, status, grid, upgradeSection, trust, footer);
  container.append(shell);

  return {
    setPrices(prices) {
      for (const [priceId, card] of cards) {
        const formatted = prices[priceId];
        card.price.textContent = formatted ?? copy.priceUnavailable;
        card.hasPrice = Boolean(formatted);
        if (!formatted) card.button.disabled = true;
      }
      status.textContent = config.checkout ? copy.localizedPriceReady : websiteCopy(currentLocale).choose;
      status.classList.toggle('action-required', !config.checkout);
    },
    enableCheckout(open) {
      for (const card of cards.values()) {
        const selected = config.checkout?.offerId === card.offer.id;
        const publicPurchaseInfo = !config.checkout && card.offer.kind === 'standalone-license';
        const publicUpgradeInfo = !config.checkout && card.offer.kind === 'license-upgrade';
        card.button.disabled =
          !card.hasPrice || (!selected && !publicPurchaseInfo && !publicUpgradeInfo);
        card.button.addEventListener('click', () => open(card.offer));
      }
    },
    setStatus(message, error = false, attention = false) {
      status.textContent = message;
      status.classList.toggle('error', error);
      status.classList.toggle('action-required', attention && !error);
      if (attention && !error) {
        status.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'center',
        });
      }
    },
  };
}

function renderCheckoutClosed(
  container: HTMLElement,
  currentLocale: SupportedLocale,
): void {
  const copy = copyFor(currentLocale);
  const closedCopy = checkoutClosedCopy(currentLocale);
  container.replaceChildren();
  const shell = element('main', 'site-shell');
  shell.append(languageToolbar(currentLocale));

  const header = element('header', 'hero');
  header.append(
    textElement('span', 'eyebrow', closedCopy.eyebrow),
    textElement('h1', '', closedCopy.title),
    textElement('p', 'hero-copy', closedCopy.description),
  );

  const status = textElement('p', 'page-status action-required', closedCopy.status);
  status.setAttribute('role', 'status');

  const grid = element('section', 'offer-grid');
  grid.setAttribute('aria-label', copy.offersLabel);
  grid.append(
    reviewOfferCard('personal', 'standalone-license', copy, closedCopy.purchaseDisabled),
    reviewOfferCard('family', 'standalone-license', copy, closedCopy.purchaseDisabled),
  );

  const upgradeSection = element('section', 'upgrade-section');
  upgradeSection.append(
    textElement('span', 'eyebrow', copy.upgradeEyebrow),
    textElement('h2', '', copy.upgradeTitle),
    textElement('p', 'section-copy', copy.upgradeDescription),
    reviewOfferCard('upgrade', 'license-upgrade', copy, closedCopy.upgradeDisabled),
  );

  const trust = element('section', 'trust-panel');
  trust.append(
    textElement('strong', '', copy.trustTitle),
    textElement('p', '', copy.trustDescription),
    textElement('p', 'refund-policy', refundPolicyCopy(currentLocale)),
  );

  const footer = element('footer', 'site-footer');
  footer.append(
    textElement('p', 'policy-intro', closedCopy.policyIntro),
    policyNavigation(currentLocale),
    textElement('p', '', copy.disclaimer),
  );

  shell.append(header, status, grid, upgradeSection, trust, footer);
  container.append(shell);
}

function reviewOfferCard(
  offerId: 'personal' | 'family' | 'upgrade',
  kind: PhotoKeeperOffer['kind'],
  copy: CheckoutCopy,
  disabledButtonText: string,
): HTMLElement {
  const localizedOffer = copy.offers[offerId];
  const root = element('article', `offer-card${offerId === 'upgrade' ? ' upgrade-card' : ''}`);
  const featureList = element('ul', 'feature-list');
  for (const feature of localizedOffer.features) {
    const item = document.createElement('li');
    item.append(textElement('span', 'checkmark', '✓'), document.createTextNode(feature));
    featureList.append(item);
  }
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'primary-button';
  button.textContent = disabledButtonText;
  button.disabled = true;
  root.append(
    textElement(
      'span',
      'offer-kicker',
      kind === 'license-upgrade' ? copy.licenseUpgrade : copy.oneTimeLicense,
    ),
    textElement('h2', '', localizedOffer.name),
    textElement('p', 'offer-description', localizedOffer.description),
    textElement('strong', 'offer-price', publishedPriceByOffer[offerId]),
    textElement('span', 'price-detail', copy.oneTimeQuantity),
    featureList,
    button,
  );
  return root;
}

function refundPolicyCopy(locale: SupportedLocale): string {
  return locale === 'zh-CN' ?
    '支付完成后 7 天（168 小时）内可在 App 一键无理由全额退款；超过 7 天、15 天（360 小时）内需提交理由和问题，人工审核。首次绑定的账号为主账号，只有主账号可以申请。基础购买和升级分别计时。' :
    'Full refunds can be requested in the App without a reason within 7 days (168 hours) of payment. From then through day 15 (360 hours), a reason and problem description are required for manual review. Only the first account bound to the license can apply. Purchases and upgrades have separate windows.';
}

function policyNavigation(currentLocale: SupportedLocale): HTMLElement {
  const copy = checkoutClosedCopy(currentLocale);
  const navigation = element('nav', 'policy-links');
  navigation.setAttribute('aria-label', copy.policiesLabel);
  for (const policy of [
    { path: '/terms/', label: copy.terms },
    { path: '/privacy/', label: copy.privacy },
    { path: '/refunds/', label: copy.refunds },
    { path: '/support/', label: copy.support },
  ]) {
    const link = document.createElement('a');
    link.href = `${publicSiteOrigin}${policy.path}`;
    link.textContent = policy.label;
    navigation.append(link);
  }
  return navigation;
}

function checkoutClosedCopy(currentLocale: SupportedLocale): {
  eyebrow: string;
  title: string;
  description: string;
  status: string;
  purchaseDisabled: string;
  upgradeDisabled: string;
  policyIntro: string;
  policiesLabel: string;
  terms: string;
  privacy: string;
  refunds: string;
  support: string;
} {
  if (currentLocale === 'zh-CN') {
    return {
      eyebrow: 'PhotoKeeper 正式版',
      title: '一次付费，备份由您掌控',
      description: '浏览您已认证的 iCloud 照片图库，并将原始媒体备份到您选择的本地存储。',
      status: '公开发布准备中。价格与政策已正式公布；在 Paddle 完成审核前，购买功能保持关闭，本页面无法发起付款。',
      purchaseDisabled: '审核通过后开放购买',
      upgradeDisabled: '审核通过后开放升级',
      policyIntro: '购买前请查看完整的产品政策：',
      policiesLabel: 'PhotoKeeper 产品政策',
      terms: '条款与 EULA',
      privacy: '隐私政策',
      refunds: '退款政策',
      support: '支持与产品生命周期',
    };
  }
  return {
    eyebrow: 'PhotoKeeper production release',
    title: 'Keep your photos close. Pay once.',
    description:
      'Browse your authenticated iCloud Photos library and back up original media to local storage you control.',
    status:
      'Public launch is in progress. Prices and policies are published, but purchasing remains disabled until Paddle completes its review. No payment can be started on this page.',
    purchaseDisabled: 'Purchasing opens after approval',
    upgradeDisabled: 'Upgrades open after approval',
    policyIntro: 'Review the complete product policies before purchasing:',
    policiesLabel: 'PhotoKeeper product policies',
    terms: 'Terms & EULA',
    privacy: 'Privacy Policy',
    refunds: 'Refund Policy',
    support: 'Support & lifecycle',
  };
}

function offerCard(
  offer: PhotoKeeperOffer,
  selected: boolean,
  copy: CheckoutCopy,
): {
  root: HTMLElement;
  button: HTMLButtonElement;
  price: HTMLElement;
  offer: PhotoKeeperOffer;
  hasPrice: boolean;
} {
  const localizedOffer = copy.offers[offer.id];
  const root = element('article', `offer-card${selected ? ' selected' : ''}`);
  if (selected) root.append(textElement('span', 'selected-badge', copy.selectedBadge));
  const price = textElement('strong', 'offer-price', '…');
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'primary-button';
  button.textContent = localizedOffer.cta;
  button.disabled = true;
  const featureList = element('ul', 'feature-list');
  for (const feature of localizedOffer.features) {
    const item = document.createElement('li');
    item.append(textElement('span', 'checkmark', '✓'), document.createTextNode(feature));
    featureList.append(item);
  }
  root.append(
    textElement(
      'span',
      'offer-kicker',
      offer.kind === 'license-upgrade' ? copy.licenseUpgrade : copy.oneTimeLicense,
    ),
    textElement('h2', '', localizedOffer.name),
    textElement('p', 'offer-description', localizedOffer.description),
    price,
    textElement('span', 'price-detail', copy.oneTimeQuantity),
    featureList,
    button,
  );
  return { root, button, price, offer, hasPrice: false };
}

function renderLoading(container: HTMLElement, currentLocale: SupportedLocale): void {
  const copy = copyFor(currentLocale);
  const { shell, panel } = stateLayout(container, currentLocale);
  panel.append(
    textElement('span', 'loader', ''),
    textElement('h1', '', copy.loadingTitle),
    textElement('p', '', copy.loadingDescription),
  );
  shell.append(panel);
}

function renderWelcome(container: HTMLElement, currentLocale: SupportedLocale): void {
  const copy = copyFor(currentLocale);
  const { shell, panel } = stateLayout(container, currentLocale, 'success-panel');
  panel.append(
    textElement('span', 'success-mark', '✓'),
    textElement('span', 'eyebrow', copy.welcomeEyebrow),
    textElement('h1', '', copy.welcomeTitle),
    textElement('p', '', copy.welcomeDescription),
    textElement('p', 'small-copy', copy.welcomeClose),
  );
  shell.append(panel);
  renderWebsiteReceipt(panel, currentLocale);
}

function renderFatal(
  container: HTMLElement,
  currentLocale: SupportedLocale,
  message: string,
): void {
  const copy = copyFor(currentLocale);
  const { shell, panel } = stateLayout(container, currentLocale, 'error-panel');
  panel.append(
    textElement('span', 'error-mark', '!'),
    textElement('h1', '', copy.fatalTitle),
    textElement('p', '', message),
  );
  shell.append(panel);
}

function stateLayout(
  container: HTMLElement,
  currentLocale: SupportedLocale,
  panelClass = '',
): { shell: HTMLElement; panel: HTMLElement } {
  container.replaceChildren();
  const shell = element('main', 'state-shell');
  const panel = element('section', `state-panel${panelClass ? ` ${panelClass}` : ''}`);
  shell.append(languageToolbar(currentLocale));
  container.append(shell);
  return { shell, panel };
}

function languageToolbar(currentLocale: SupportedLocale): HTMLElement {
  const copy = copyFor(currentLocale);
  const toolbar = element('div', 'page-toolbar');
  const label = element('label', 'language-control');
  const labelText = textElement('span', 'language-label', copy.languageLabel);
  labelText.setAttribute('aria-hidden', 'true');
  const select = document.createElement('select');
  select.className = 'language-select';
  select.setAttribute('aria-label', copy.languageLabel);
  for (const option of publicLocaleOptions) {
    const item = document.createElement('option');
    item.value = option.code;
    item.textContent = option.label;
    item.selected = option.code === currentLocale;
    select.append(item);
  }
  select.addEventListener('change', () => {
    const nextLocale = matchSupportedLocale(select.value);
    if (!nextLocale || nextLocale === currentLocale) return;
    storeLocale(nextLocale);
    window.location.assign(urlWithLocale(window.location.href, nextLocale).toString());
  });
  label.append(textElement('span', 'language-icon', '文/A'), labelText, select);
  toolbar.append(label);
  return toolbar;
}

function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(localeStorageKey);
  } catch {
    return null;
  }
}

function storeLocale(currentLocale: SupportedLocale): void {
  try {
    window.localStorage.setItem(localeStorageKey, currentLocale);
  } catch {
    // Language selection still works through the URL when storage is unavailable.
  }
}

function applyDocumentLocale(currentLocale: SupportedLocale): void {
  const copy = copyFor(currentLocale);
  document.documentElement.lang = currentLocale;
  document.documentElement.dir = directionFor(currentLocale);
  document.title = copy.metaTitle;
  document
    .querySelector<HTMLMetaElement>('meta[name="description"]')
    ?.setAttribute('content', copy.metaDescription);
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const value = document.createElement(tag);
  if (className) value.className = className;
  return value;
}

function textElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text: string,
): HTMLElementTagNameMap[K] {
  const value = element(tag, className);
  value.textContent = text;
  return value;
}

class CheckoutConfigError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super('checkout_config_unavailable');
  }
}
